import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import {CookieService} from "ngx-cookie";
import {ActivatedRoute, Router} from "@angular/router";
import {Subscription} from "rxjs";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {filter, first} from "rxjs/operators";
import {AiAssistantService} from "../../../../../ai-assistant/service/ai-assistant.service";
import {AiBuilderService} from "../../../../../websites/website/ai-builder/service/ai-builder.service";
import {IDsDropdownOption} from "../../../../../shared/components/ds-dropdown/ds-dropdown-option";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {DbResponse} from "../../../../../shared/interceptors/db-response";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {TypeUtils} from "../../../../../shared/utils/type-utils";
import {BuilderHistoryService} from "../../../../builder-history.service";
import {BuilderSectionsInteractionService} from "../../../../builder-sections-interaction.service";
import {DbActiveModal} from "../../../../../modules/modal/modal-ref";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {User} from '../../../../../user/user';
import {BuilderStepsEnum} from "../../../../builder-tour.enum";
import {Config} from "../../../../../config/config";

@Component({
    selector: 'db-choose-store-type',
    templateUrl: './choose-store-type.component.html',
    styleUrls: ['./choose-store-type.component.scss'],
    standalone: false
})
export class ChooseStoreTypeComponent implements OnInit, AfterViewInit {

  @Input() storeTypesForView: any;
  @Input() popupMode: boolean;
  @Input() hasEcommerceService: boolean = true;
  @Input() selectedCompanyType: any;
  @Input() storeTypes: any;
  @Input() selectedCategoryType: string;
  @Input() selectedBusinessCategory: string;
  @Input() user: User;
  @Input() uniqueId: string;
  @Input() isMobile: boolean;

  websiteType: string = '';
  loading: boolean = false;
  builderStep = BuilderStepsEnum;

  @Output() back = new EventEmitter<void>();
  @Output() mobileNext = new EventEmitter<void>();
  @Output() storeTypeSelected = new EventEmitter<{ type: string; group: string }>();
  @Output() categoryType = new EventEmitter<string>();
  @Output() companyTypeSelected = new EventEmitter<string>();
  @Output() companyTypeSearchedList = new EventEmitter<any[]>();

  private _categoriesRequestSubscription: Subscription;

  constructor(
    private readonly router: Router,
    private readonly _route: ActivatedRoute,
    private readonly _cookieService: CookieService,
    private readonly _builderService: AiBuilderService,
    private _aiAssistantService: AiAssistantService,
    private _builderHistoryService: BuilderHistoryService,
    private builderInteractionsService: BuilderSectionsInteractionService,
    private _cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    private elRef: ElementRef,
    private _activeModal: DbActiveModal,
    private _googleAnalyticsService: GoogleAnalyticsService,
  ) {}

  ngOnInit() {
    this.builderInteractionsService.disableNextButton.next(!this.selectedCompanyType);
    this.builderInteractionsService.setBackAction(() => {
      this.backToPreviousStep();
    });
    this.isMobile && this.checkMobileData();
  }

  ngAfterViewInit() {
    const inputs = this.elRef.nativeElement.querySelectorAll('input, textarea');

    inputs.forEach((input: HTMLElement) => {
      // Disable zoom on the first focus
      this.renderer.listen(input, 'focus', () => this.disableZoom());
      this.renderer.listen(input, 'touchstart', () => this.disableZoom());
    });
  }

  // Disable zoom by setting viewport to maximum scale 1.0
  disableZoom() {
    const viewport = document.querySelector("meta[name=viewport]");
    if (viewport) {
      viewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
      );
    }
  }

  checkMobileData() {
    const state = this._builderHistoryService.getFormData();

    if (state?.oldFormData) {
      this._cdr.detectChanges();
      this.storeTypesForView = state?.companyTypes;
      this.selectStoreType({value: state?.oldFormData?.business_type, group: state?.oldFormData?.business_category});
    }
  }

  public searchCategory($event) {
    if (this._categoriesRequestSubscription) {
      this._categoriesRequestSubscription.unsubscribe();
      this._categoriesRequestSubscription = null;
    }
    if ($event === '') {
      return this.storeTypesForView = this.storeTypes;
    }
    this._categoriesRequestSubscription = this._builderService.searchCategories($event, true, true)
      .pipe(filter((response: DbResponse): boolean => !!response),
        first())
      .subscribe((response: DbResponse): void => {
        if (response.success) {
          const searchedResult = response.data.map((companyType) => (
            {
              label: companyType.title,
              highlightedLabel: companyType.title.replace(new RegExp($event, "gi"), (match) => `<span class="ds-highlighted">${match}</span>`),
              value: companyType.title,
              type: companyType.type,
              group: companyType.group
            }
          ));

          // const uniqueResults = searchedResult.filter(newItem =>
          //   !this.companyTypesForView.some(existingItem => existingItem.label === newItem.label)
          // );
          const combinedArray = [...searchedResult, ...this.storeTypesForView];
          const dataToPass = {companyTypes: this.storeTypesForView};
          this.isMobile && this._builderHistoryService.saveFormData(dataToPass);

          this.storeTypesForView = combinedArray.filter((item, index, self) =>
              index === self.findIndex((t) => (
                t.value === item.value
              ))
          );
        } else {
          this.storeTypesForView = this.storeTypes;
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        }
        this._cdr.detectChanges();
      });
  }

  public selectStoreType(option: { value: IDsDropdownOption['value'], group: IDsDropdownOption['group'] }) {
    this.selectedCompanyType = option?.value as string;
    this.selectedBusinessCategory = option?.group as string;
    this.builderInteractionsService.businessCategory.next(this.selectedBusinessCategory );
    this.builderInteractionsService.disableNextButton.next(false);
    this.companyTypeSelected.emit(this.selectedCompanyType);
    this.companyTypeSearchedList.emit(this.storeTypesForView);
    this.categoryType.emit(this.storeTypesForView[0].type);
  }

  public proceedToNextQuestion(): void {
    this.sendEvent(this.popupMode ? 'Dashboard Action' : 'Onboarding', 'Section-based AI Flow - Question answered', JSON.stringify(['business_type']), this.selectedCompanyType, this.storeTypesForView[0].type === 'basic' ? 'Business' : 'Ecommerce', this.selectedBusinessCategory);
  }

  private sendEvent(category: string, action: string, label: string, companyType: string, websiteType, groupType): void {
    const analyticsData = {
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      companyType: companyType,
      websiteType: websiteType,
      businessCategory: groupType,
      uniqueId: this.uniqueId,
      isMobile: this.isMobile
    };

    if (!this.user) {
      this._googleAnalyticsService.sendAnalyticsEventWithCoreWithoutUserData(analyticsData);
    } else {
      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    }
  }

  backToPreviousStep(): void {
    this.popupMode ?  this._activeModal.close() : this.back.emit();
  }

}

